/**
 * TickerLogo - Displays a company logo for a stock ticker.
 *
 * Uses logo.dev API for logo images with a styled text-initial fallback
 * when the logo is unavailable (404, small-cap tickers, etc.).
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';

interface TickerLogoProps {
  symbol: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeMap = {
  xs: { container: 'w-5 h-5', text: 'text-[8px]', px: 20 },
  sm: { container: 'w-6 h-6', text: 'text-[9px]', px: 24 },
  md: { container: 'w-8 h-8', text: 'text-xs', px: 32 },
  lg: { container: 'w-10 h-10', text: 'text-sm', px: 40 },
};

const fallbackColors = [
  'bg-blue-500/20 text-blue-400',
  'bg-emerald-500/20 text-emerald-400',
  'bg-purple-500/20 text-purple-400',
  'bg-amber-500/20 text-amber-400',
  'bg-cyan-500/20 text-cyan-400',
  'bg-rose-500/20 text-rose-400',
  'bg-indigo-500/20 text-indigo-400',
  'bg-teal-500/20 text-teal-400',
];

/** Deterministic color from ticker symbol for the fallback. */
function getTickerColor(symbol: string): string {
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) {
    hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
  }
  return fallbackColors[Math.abs(hash) % fallbackColors.length];
}

const LOGO_DEV_TOKEN = import.meta.env.VITE_LOGO_DEV_TOKEN || '';

function getLogoUrl(symbol: string): string {
  return `https://img.logo.dev/ticker/${symbol.toUpperCase()}?token=${LOGO_DEV_TOKEN}&format=png`;
}

export function TickerLogo({ symbol, name, size = 'sm', className }: TickerLogoProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const { container, text } = sizeMap[size];

  if (imgFailed || !LOGO_DEV_TOKEN) {
    return (
      <div
        className={cn(
          'rounded-full flex items-center justify-center font-bold shrink-0',
          container, text,
          getTickerColor(symbol),
          className,
        )}
        title={name || symbol}
      >
        {symbol.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={getLogoUrl(symbol)}
      alt={name || symbol}
      className={cn('rounded-full object-cover shrink-0 bg-surface-light', container, className)}
      onError={() => setImgFailed(true)}
      loading="lazy"
    />
  );
}
